import { Box, Button, Typography } from '@mui/material';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import PodcastsOutlinedIcon from '@mui/icons-material/PodcastsOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import FacebookOutlinedIcon from '@mui/icons-material/FacebookOutlined';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import YouTubeIcon from '@mui/icons-material/YouTube';
import GoogleIcon from '@mui/icons-material/Google';
import InstagramIcon from '@mui/icons-material/Instagram';
import XIcon from '@mui/icons-material/X';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { GhlService } from '../../../services/ghl/ghl.service';

interface SourceOption {
  id: string;
  label: string;
  icon: ReactNode;
}

const sourceOptions: SourceOption[] = [
  { id: 'news', label: 'In the news', icon: <NewspaperOutlinedIcon sx={{ fontSize: 20 }} /> },
  { id: 'podcast', label: 'Podcast', icon: <PodcastsOutlinedIcon sx={{ fontSize: 20 }} /> },
  { id: 'newsletter_blog', label: 'Newsletter or Blog', icon: <MailOutlineOutlinedIcon sx={{ fontSize: 20 }} /> },
  { id: 'work', label: 'From work', icon: <WorkOutlineOutlinedIcon sx={{ fontSize: 20 }} /> },
  { id: 'ai_tools', label: 'ChatGPT, Claude, etc.', icon: <SmartToyOutlinedIcon sx={{ fontSize: 20 }} /> },
  { id: 'friends_school', label: 'Friends or School', icon: <GroupOutlinedIcon sx={{ fontSize: 20 }} /> },
  { id: 'facebook', label: 'Facebook', icon: <FacebookOutlinedIcon sx={{ fontSize: 20 }} /> },
  { id: 'tiktok', label: 'TikTok', icon: <MusicNoteOutlinedIcon sx={{ fontSize: 20 }} /> },
  { id: 'youtube', label: 'YouTube', icon: <YouTubeIcon sx={{ fontSize: 20 }} /> },
  { id: 'google', label: 'Google', icon: <GoogleIcon sx={{ fontSize: 20 }} /> },
  { id: 'instagram', label: 'Instagram', icon: <InstagramIcon sx={{ fontSize: 20 }} /> },
  { id: 'x', label: 'X', icon: <XIcon sx={{ fontSize: 20 }} /> },
  { id: 'linkedin', label: 'LinkedIn', icon: <LinkedInIcon sx={{ fontSize: 20 }} /> },
  { id: 'dont_remember', label: "Don't remember", icon: <HelpOutlineOutlinedIcon sx={{ fontSize: 20 }} /> },
  { id: 'other', label: 'Other', icon: <MoreHorizOutlinedIcon sx={{ fontSize: 20 }} /> },
];

interface OnboardingSourceStepProps {
  email: string;
  initialSelected: string;
  onComplete: (source: string, ghlContactId: string) => void;
  onSkip: () => void;
  onBack: () => void;
  ghlContactId: string;
}

export default function OnboardingSourceStep({ email, initialSelected, onComplete, onSkip, onBack, ghlContactId }: OnboardingSourceStepProps) {
  const [selected, setSelected] = useState<string>(initialSelected || '');

  useEffect(() => {
    setSelected(initialSelected || '');
  }, [initialSelected]);

  return (
    <Box sx={{ maxWidth: 740 }}>
      <Typography
        component="h1"
        sx={{
          textAlign: 'center',
          color: '#111827',
          fontSize: { xs: '1.25rem', sm: '1.75rem' },
          fontWeight: 700,
          letterSpacing: '-0.01em',
          mb: 2.5,
        }}
      >
        How did you hear about EasyWebinar?
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' },
          gap: 1.1,
          mb: 3,
        }}
      >
        {sourceOptions.map((option) => {
          const isSelected = selected === option.id;
          return (
            <Box
              key={option.id}
              onClick={() => setSelected(option.id)}
              sx={{
                border: isSelected ? '1px solid #111827' : '1px solid #E5E7EB',
                bgcolor: isSelected ? '#F3F4F6' : '#FFFFFF',
                borderRadius: '10px',
                px: 1.2,
                py: 1.1,
                minHeight: 56,
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': {
                  borderColor: isSelected ? '#111827' : '#C4C6F0',
                },
              }}
            >
              <Box sx={{ color: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {option.icon}
              </Box>
              <Typography sx={{ color: '#111827', fontSize: {xs: "12px", sm: "14px"}, fontWeight: 500, lineHeight: 1.2 }}>
                {option.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Button
        fullWidth
        variant="contained"
        disabled={!selected}
        onClick={async () => {
          let ghlContactIdToUpdate = '';
          if (!ghlContactId) {
            ghlContactIdToUpdate = await GhlService.getContactByEmail(email).then((data: any) => data?.id);
          } else {
            ghlContactIdToUpdate = ghlContactId;
          }
          await GhlService.updateContactFields({ contactId: ghlContactIdToUpdate, fields: [{ key: 'resource', value: selected }] });
          onComplete(selected, ghlContactIdToUpdate);
        }}
        sx={{
          textTransform: 'none',
          borderRadius: '10px',
          py: 1,
          fontSize: '0.92rem',
          fontWeight: 600,
          color: '#FFFFFF',
          bgcolor: '#4058fb',
          boxShadow: 'none',
          '&:hover': { bgcolor: '#3442bd', boxShadow: 'none' },
          '&.Mui-disabled': {
            bgcolor: '#4058fb',
            color: '#C5C7D0',
            opacity: 0.6,
            cursor: 'not-allowed',
            boxShadow: 'none',
          },
        }}
      >
        Continue
      </Button>
      <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button variant="text" onClick={onBack} sx={{ textTransform: 'none', color: '#6066E9', fontSize: '0.9rem', fontWeight: 500 }}>
          Back
        </Button>
        <Button variant="text" onClick={onSkip} sx={{ textTransform: 'none', color: '#6B7280', fontSize: '0.9rem', fontWeight: 500 }}>
          Skip
        </Button>
      </Box>
    </Box>
  );
}
